/* eslint-disable import/named */
import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button, Table,
  Modal, Skeleton,
  Input,
} from 'antd';
import { PropTypes } from 'prop-types';
import { PlusOutlined } from '@ant-design/icons';
import InfoView from '../../components/common/InfoView';
import { APIColumns } from './Columns';
import APIKeyForm from './APIKeyForm';
import {
  generateAPIKey,
  getAPIKeys,
  updateAPIKey,
  deleteAPIKey,
} from '../../redux/actions/Data';
import { MERCHANT_DETAILS } from '../../constants/resources';
import Can from '../../util/can';

const APIKeyCard = ({ merchantId }) => {
  const dispatch = useDispatch();
  const {
    data: apiKeys,
    loading,
    creatingKey,
    latestKey,
    updatingKey,
  } = useSelector(({ data }) => data.apiKeys);
  const [showForm, setShowForm] = useState(false);
  const [isKeyGenerated, setIsKeyGenerated] = useState(false);
  const [editableRecord, setEditableRecord] = useState(null);
  const [selectedTrafficTypes, setSelectedTrafficTypes] = useState('');
  const form = useRef();

  useEffect(() => {
    dispatch(getAPIKeys(merchantId));
  }, [dispatch]);

  const handleShowAPIModal = () => {
    if (form.current) {
      form.current.resetFields();
    }
    setEditableRecord(null);
    setShowForm(true);
  };

  const handleEditMerchantDetail = (record) => {
    setShowForm(true);
    setEditableRecord({ ...record, merchant_type: record.merchant_type?._id });
    setTimeout(() => {
      form.current.setFieldsValue({ ...record, merchant_type: record.merchant_type?._id });
    }, 300);
  };

  const handleDeleteMerchantDetail = (record) => {
    dispatch(deleteAPIKey(merchantId, record._id));
  };

  const handleCloseAPIModal = () => {
    setShowForm(false);
  };

  const handleChangeTrafficType = (value) => {
    setSelectedTrafficTypes(value);
  };

  const handleGenerateAPIKey = () => {
    if (form.current) {
      form.current.validateFields().then((values) => {
        const data = {
          ...values,
          merchant: merchantId,
        };

        if (editableRecord) {
          dispatch(updateAPIKey(editableRecord._id, values));
          setTimeout(() => setShowForm(false), 2000);
        } else {
          dispatch(generateAPIKey(data));
          setIsKeyGenerated(true);
          setShowForm(false);
        }
      });
    }
  };

  const handleSearch = (searchValue) => {
    const _searchValue = searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1');
    if (_searchValue) {
      const searchFields = ['api_key'];
      let searchParams = JSON.stringify(
        searchFields.map((field) => ({
          [field]: { $regex: _searchValue.trim(), $options: 'i' },
        })),
      );
      if (!Number.isNaN(Number(_searchValue))) {
        searchParams = JSON.stringify([{ fixed_charge: Number(_searchValue) }, {
          percentage_charge: Number(_searchValue),
        }]);
      }
      const search = `filter={"$or": ${searchParams}}`;
      // if (merchantId) {
      //   dispatch(fetchMerchantChargesById(merchantId, search));
      // } else {
      //   dispatch(fetchMerchantCharges(search));
      // }
    } else {
      // const pagination = `page=${current}&limit=${pageSize}`;
      // if (merchantId) {
      //   dispatch(fetchMerchantChargesById(merchantId, pagination));
      // } else {
      //   dispatch(fetchMerchantCharges(pagination));
      // }
    }
  };

  const tableProps = {
    loading: loading || creatingKey,
    pagination: false,
    rowKey: '_id',
    scroll: { x: 1300 },
    columns: APIColumns(handleEditMerchantDetail, handleDeleteMerchantDetail),
    dataSource: apiKeys,
  };

  return (
    <>
      <div className="gx-d-flex justify-content-center">
        {merchantId && (
          <Can I="create" a={MERCHANT_DETAILS}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => handleShowAPIModal()}
            >
              Generate API Key
            </Button>

          </Can>
        )}
        <Input.Search
          placeholder="Search api key"
          enterButton
          allowClear
          onSearch={handleSearch}
        />
      </div>
      <div className="gx-d-flex justify-content-center">
        <Table className="gx-table-responsive" {...tableProps} />
      </div>
      <Modal
        title={editableRecord ? 'Update API token' : 'Generate API Token'}
        visible={showForm}
        okText={editableRecord ? 'Update' : 'Generate'}
        onOk={handleGenerateAPIKey}
        confirmLoading={updatingKey}
        onCancel={handleCloseAPIModal}
      >
        <APIKeyForm {...{
          form,
          handleChangeTrafficType,
          selectedTrafficTypes,
        }}
        />
      </Modal>
      <Modal
        title="API Token"
        width={660}
        visible={isKeyGenerated}
        onOk={() => setIsKeyGenerated(false)}
        className="api-modal"
      >
        {creatingKey && <Skeleton avatar paragraph={{ rows: 2 }} />}
        {!creatingKey && (Object.keys(latestKey).length > 0) && (
        <div className="api-info-container">
          <div className="api-info">Please copy and store the information below securely. Once the modal is closed, you won&apos;t be able to see this information again</div>
          <div>
            <span className="label">API KEY: </span>
            <span>{latestKey.api_key}</span>
          </div>
          <div>
            <span className="label">API SECRET: </span>
            <span>{latestKey.plain_api_secret}</span>
          </div>
        </div>
        ) }
      </Modal>
      <InfoView />
    </>
  );
};

APIKeyCard.propTypes = {
  merchantId: PropTypes.string,
};

APIKeyCard.defaultProps = {
  merchantId: null,
};

export default APIKeyCard;
